/**
 * Optional Shopify discovery steps (2026-10-28). Shopify serves its own
 * /llms.txt, /agents.md and /.well-known/ucp on every store and reserves
 * /.well-known, so Sill's documents live under /apps/sill (the app
 * proxy). Two steps point agents there once the app is connected:
 *
 *   - `ShopifyAgentsMdStep`  — an agents.md.liquid template to paste.
 *   - `ShopifyAgentRedirectStep` — a URL redirect from /agent.
 *
 * `ShopifyCloudflareObserveStep` is the grey-cloud route: with the
 * store record proxied through Cloudflare, the Sill Worker can run in
 * front of Shopify and observe every agent without the app.
 *
 * ADR-0212 pattern: one sentence + one button + Collapsible per step.
 */

import { ArrowRight, Check, Copy } from 'lucide-react';
import { useState } from 'react';

import { Collapsible } from '../../components/atoms/index.js';
import { useC } from '../../lib/theme.js';
import {
  SHOPIFY_AGENTS_MD_NOTE,
  SHOPIFY_AGENTS_MD_SENTENCE,
  SHOPIFY_AGENTS_MD_STEPS,
  SHOPIFY_AGENTS_MD_TITLE,
  SHOPIFY_AGENT_REDIRECT_SENTENCE,
  SHOPIFY_AGENT_REDIRECT_STEPS,
  SHOPIFY_AGENT_REDIRECT_TITLE,
  SHOPIFY_CF_OBSERVE_LABEL,
  SHOPIFY_CF_OBSERVE_PENDING,
  SHOPIFY_CF_OBSERVE_SAFE,
  SHOPIFY_CF_OBSERVE_SENTENCE,
  SHOPIFY_CF_OBSERVE_STEPS,
  SHOPIFY_CF_OBSERVE_TITLE,
  SHOPIFY_SAFE_WORKER_AVAILABLE,
  shopifyAgentsMdTemplate,
} from './install-copy.js';

const monoFont = "'JetBrains Mono', monospace";
const sansFont = "'Geist', sans-serif";

function StepShell({ kicker, title, sentence, children, testid }: {
  kicker: string;
  title: string;
  sentence: string;
  children: React.ReactNode;
  testid: string;
}): JSX.Element {
  const C = useC();
  return (
    <div className="mt-4 p-3" style={{ border: `1px solid ${C.borderSoft}`, borderRadius: 2 }} data-testid={testid}>
      <div style={{ fontFamily: monoFont, fontSize: 10, letterSpacing: 2, color: C.muted, marginBottom: 6 }}>{kicker}</div>
      <div style={{ fontFamily: sansFont, fontSize: 14, fontWeight: 500, color: C.text, marginBottom: 4 }}>{title}</div>
      <p style={{ fontFamily: sansFont, fontSize: 12.5, color: C.muted, lineHeight: 1.6, margin: '0 0 10px', maxWidth: 560 }}>{sentence}</p>
      {children}
    </div>
  );
}

function Steps({ steps }: { steps: readonly string[] }): JSX.Element {
  const C = useC();
  return (
    <ol style={{ listStyle: 'decimal', paddingLeft: 18, margin: 0, fontFamily: sansFont, fontSize: 12.5, color: C.text, lineHeight: 1.7 }}>
      {steps.map((s) => <li key={s}>{s}</li>)}
    </ol>
  );
}

export function ShopifyAgentsMdStep({ domain }: { domain: string }): JSX.Element {
  const C = useC();
  const [copied, setCopied] = useState(false);
  const template = shopifyAgentsMdTemplate(domain);
  const copy = (): void => {
    if (typeof navigator !== 'undefined' && navigator.clipboard) {
      void navigator.clipboard.writeText(template);
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
    }
  };
  return (
    <StepShell kicker="OPTIONAL" title={SHOPIFY_AGENTS_MD_TITLE} sentence={SHOPIFY_AGENTS_MD_SENTENCE} testid="shopify-agents-md-step">
      <button
        type="button"
        onClick={copy}
        className="inline-flex items-center gap-1.5 px-3 py-1.5"
        style={{ background: 'transparent', border: `1px solid ${C.border}`, color: C.text, fontFamily: sansFont, fontSize: 12, borderRadius: 2, cursor: 'pointer', marginBottom: 10 }}
        data-testid="shopify-agents-md-copy"
      >
        {copied ? <Check size={12} /> : <Copy size={12} />}
        {copied ? 'Copied' : 'Copy template'}
      </button>
      <div>
        <Collapsible variant="inline" label="How to add it">
          <Steps steps={SHOPIFY_AGENTS_MD_STEPS} />
          <p style={{ fontFamily: sansFont, fontSize: 12, color: C.muted, lineHeight: 1.6, margin: '10px 0' }}>{SHOPIFY_AGENTS_MD_NOTE}</p>
          <pre
            style={{ fontFamily: monoFont, fontSize: 11, color: C.text, background: 'transparent', border: `1px solid ${C.borderSoft}`, borderRadius: 2, padding: 10, whiteSpace: 'pre-wrap', wordBreak: 'break-word', margin: 0 }}
            data-testid="shopify-agents-md-template"
          >
            {template}
          </pre>
        </Collapsible>
      </div>
    </StepShell>
  );
}

export function ShopifyAgentRedirectStep(): JSX.Element {
  return (
    <StepShell kicker="OPTIONAL" title={SHOPIFY_AGENT_REDIRECT_TITLE} sentence={SHOPIFY_AGENT_REDIRECT_SENTENCE} testid="shopify-agent-redirect-step">
      <Collapsible variant="inline" label="How to add the redirect">
        <Steps steps={SHOPIFY_AGENT_REDIRECT_STEPS} />
      </Collapsible>
    </StepShell>
  );
}

export function ShopifyCloudflareObserveStep({ onSetUpWorker }: { onSetUpWorker: () => void }): JSX.Element {
  const C = useC();
  // Shown on every Shopify site: Shopify's own Cloudflare answers every
  // store, so headers cannot tell whether the merchant's DNS is on
  // Cloudflare. The merchant opens it if theirs is.
  return (
    <div className="mt-4" data-testid="shopify-cf-observe-step">
      <Collapsible variant="inline" label={SHOPIFY_CF_OBSERVE_LABEL}>
        <div className="p-3" style={{ border: `1px solid ${C.borderSoft}`, borderRadius: 2 }}>
          <div style={{ fontFamily: sansFont, fontSize: 14, fontWeight: 500, color: C.text, marginBottom: 4 }}>{SHOPIFY_CF_OBSERVE_TITLE}</div>
          <p style={{ fontFamily: sansFont, fontSize: 12.5, color: C.muted, lineHeight: 1.6, margin: '0 0 10px', maxWidth: 560 }}>{SHOPIFY_CF_OBSERVE_SENTENCE}</p>
          <p style={{ fontFamily: sansFont, fontSize: 12.5, color: C.muted, lineHeight: 1.6, margin: '0 0 10px', maxWidth: 560 }}>{SHOPIFY_CF_OBSERVE_SAFE}</p>
          <Steps steps={SHOPIFY_CF_OBSERVE_STEPS} />
          {SHOPIFY_SAFE_WORKER_AVAILABLE ? (
            <button
              type="button"
              onClick={onSetUpWorker}
              className="inline-flex items-center gap-1.5 px-3 py-2"
              style={{ background: 'transparent', color: C.text, border: `1px solid ${C.border}`, borderRadius: 2, fontFamily: sansFont, fontWeight: 500, fontSize: 12.5, cursor: 'pointer', marginTop: 10 }}
              data-testid="shopify-cf-observe-switch"
            >
              Set up the Cloudflare Worker <ArrowRight size={13} />
            </button>
          ) : (
            <p style={{ fontFamily: sansFont, fontSize: 12, color: C.amberFg, lineHeight: 1.6, margin: '10px 0 0', maxWidth: 560 }} data-testid="shopify-cf-observe-pending">
              {SHOPIFY_CF_OBSERVE_PENDING}
            </p>
          )}
        </div>
      </Collapsible>
    </div>
  );
}
